import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Contact } from '@personical/cc-shared-types';
import { useApi } from '../components/api-context.js';
import { Avatar } from '../components/Avatar.js';

export function TrashPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [items, setItems] = useState<Contact[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setItems(null);
    api
      .list({ trashed: true })
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function restore(c: Contact) {
    setBusyId(c.id);
    try {
      await api.restore(c.id);
      setItems((prev) => prev?.filter((x) => x.id !== c.id) ?? null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function hardDelete(c: Contact) {
    if (!confirm(`Permanently delete "${c.displayName}"? This cannot be undone.`)) return;
    setBusyId(c.id);
    try {
      await api.remove(c.id, { hard: true });
      setItems((prev) => prev?.filter((x) => x.id !== c.id) ?? null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-2xl font-semibold">Trash</h2>
        <button
          type="button"
          onClick={() => navigate('/contacts')}
          className="text-sm text-mid hover:text-strong"
        >
          ← Back to contacts
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-sm rounded p-3">{error}</div>
      )}

      {items === null ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <div className="surface rounded-2xl shadow p-8 text-center">
          <p className="text-sm text-mid">Trash is empty.</p>
        </div>
      ) : (
        <ul className="surface rounded-2xl shadow divide-y divide-divider">
          {items.map((c) => (
            <li key={c.id} className="flex items-start gap-3 px-5 py-4 flex-wrap">
              <Avatar name={c.displayName} photoUrl={c.photoUrl} />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{c.displayName}</p>
                <p className="text-xs text-muted">
                  {[c.jobTitle, c.department, c.company].filter(Boolean).join(' · ') || '—'}
                </p>
                {c.deletedAt && (
                  <p className="text-xs text-faint mt-1">
                    Deleted {new Date(c.deletedAt).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => restore(c)}
                  disabled={busyId === c.id}
                  className="btn-secondary px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  Restore
                </button>
                <button
                  type="button"
                  onClick={() => hardDelete(c)}
                  disabled={busyId === c.id}
                  className="rounded-lg border border-red-300 dark:border-red-900 bg-white dark:bg-slate-900 text-red-700 dark:text-red-300 px-3 py-1.5 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  Delete forever
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
